import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Download, FileText, Sheet } from 'lucide-react';

/**
 * Export Portfolio modal + button.
 *
 * Pre-export prompt lets the user pick:
 *   - Format: CSV or XLSX
 *   - Whether Lease-in-Progress counts as occupied
 *   - Whether to include Lease-in-Progress units in the per-unit listing
 *   - Whether to include the Status column on each unit row
 *
 * The resulting file has:
 *   - Header rows: portfolio name, export date, LIP-counted-as-occupied flag
 *   - For each building: unit rows, then a building total row with occupancy %
 *   - At the bottom: a portfolio-wide total row with overall occupancy %
 */
export default function ExportPortfolio({
  portfolioName,
  unitsByAddress,
  defaultIncludeLip = true,
  buttonClassName = 'btn-secondary',
}) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState('csv');
  const [countLip, setCountLip] = useState(defaultIncludeLip);
  const [includeLipRows, setIncludeLipRows] = useState(true);
  const [includeStatus, setIncludeStatus] = useState(true);
  const [busy, setBusy] = useState(false);

  const handleExport = () => {
    setBusy(true);
    try {
      const safeName = (portfolioName || 'portfolio').replace(/[^\w-]+/g, '_');
      const dateStr = new Date().toISOString().slice(0, 10);
      const filenameBase = `unitrelay_${safeName}_${dateStr}`;

      const rows = buildRows(unitsByAddress, {
        portfolioName,
        countLip,
        includeLipRows,
        includeStatus,
      });

      if (format === 'csv') {
        const csv = rowsToCsv(rows);
        downloadBlob(csv, `${filenameBase}.csv`, 'text/csv;charset=utf-8');
      } else {
        const ws = XLSX.utils.aoa_to_sheet(rows);
        // Auto-size columns based on max width per column
        const colWidths = computeColumnWidths(rows);
        ws['!cols'] = colWidths.map((w) => ({ wch: Math.min(60, Math.max(8, w + 2)) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Portfolio');
        XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      }
      setOpen(false);
    } catch (e) {
      console.error('Export failed', e);
      window.alert('Export failed. See browser console for details.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className={buttonClassName}
        onClick={() => setOpen(true)}
        data-testid="export-portfolio-button"
      >
        <Download size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
        Export
      </button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="export-portfolio-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Portfolio</DialogTitle>
            <DialogDescription>
              Choose a format and what to include. The file will contain a per-building
              breakdown with occupancy and a portfolio-wide total at the bottom.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>File format</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setFormat('csv')}
                  data-testid="export-format-csv"
                  className={format === 'csv' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <FileText size={14} /> CSV
                </button>
                <button
                  type="button"
                  onClick={() => setFormat('xlsx')}
                  data-testid="export-format-xlsx"
                  className={format === 'xlsx' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Sheet size={14} /> Excel (.xlsx)
                </button>
              </div>
            </div>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={countLip}
                onChange={(e) => setCountLip(e.target.checked)}
                data-testid="export-count-lip-checkbox"
                style={{ marginTop: 3 }}
              />
              <span>
                <strong style={{ fontSize: 13 }}>Count Lease-in-Progress as occupied</strong>
                <br />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Affects the occupancy % shown in the file footers.
                </span>
              </span>
            </label>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeLipRows}
                onChange={(e) => setIncludeLipRows(e.target.checked)}
                data-testid="export-include-lip-rows-checkbox"
                style={{ marginTop: 3 }}
              />
              <span>
                <strong style={{ fontSize: 13 }}>Include Lease-in-Progress units as rows</strong>
                <br />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  When unchecked, LIP units are excluded from the unit listing
                  but still affect the totals if you’ve checked &ldquo;Count Lease-in-Progress as occupied&rdquo;.
                </span>
              </span>
            </label>

            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeStatus}
                onChange={(e) => setIncludeStatus(e.target.checked)}
                data-testid="export-include-status-checkbox"
                style={{ marginTop: 3 }}
              />
              <span>
                <strong style={{ fontSize: 13 }}>Include unit Status column</strong>
                <br />
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  Adds the per-unit status (Current, Vacant-Ready, etc.) to each row.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="export-cancel-button">
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={busy} data-testid="export-download-button">
              <Download size={14} style={{ marginRight: 6 }} />
              {busy ? 'Preparing…' : 'Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------- helpers ----------

/** Status is "occupied" when Current, plus Lease-in-Progress when the user opts in. */
function isOccupied(unit, countLip) {
  const s = (unit.status || '').toLowerCase();
  if (s === 'current') return true;
  if (s === 'lease-in-progress' && countLip) return true;
  return false;
}

function buildRows(unitsByAddress, { portfolioName, countLip, includeLipRows, includeStatus }) {
  const rows = [];
  rows.push(['Unit Relay — Portfolio Export']);
  rows.push(['Portfolio', portfolioName || '']);
  rows.push(['Exported', new Date().toLocaleString()]);
  rows.push(['Lease-in-Progress counted as occupied', countLip ? 'Yes' : 'No']);
  rows.push([]);

  const headerCols = ['Building', 'Unit', 'BD/BA'];
  if (includeStatus) headerCols.push('Status');
  headerCols.push('Sqft', 'Vacant Unit Status', 'Listing URL');

  let portfolioOccupied = 0;
  let portfolioDenom = 0;
  const buildings = Object.entries(unitsByAddress);
  const multiBuilding = buildings.length > 1;

  buildings.forEach(([address, addressUnits], idx) => {
    // Per-building section header + column header
    rows.push([address]);
    rows.push(headerCols);

    const rentable = addressUnits.filter((u) => !u.hidden); // denominator: non-hidden (Offline included)
    let buildingOccupied = 0;

    addressUnits.forEach((unit) => {
      if (unit.hidden) return;
      const statusLc = (unit.status || '').toLowerCase();
      const isLip = statusLc === 'lease-in-progress';
      if (isLip && !includeLipRows) {
        // skip the row but still count for totals below
      } else {
        const row = [address, unit.unit ?? '', unit.bd_ba ?? ''];
        if (includeStatus) row.push(unit.status ?? '');
        row.push(unit.sqft ?? '', unit.vacant_unit_status ?? '', unit.listing_url ?? '');
        rows.push(row);
      }
      if (isOccupied(unit, countLip)) buildingOccupied += 1;
    });

    const denom = rentable.length;
    const pct = denom > 0 ? Math.round((buildingOccupied / denom) * 100) : 0;

    // Per-building total (always show when multi-building; also useful single)
    const totalLabel = multiBuilding ? 'Building total' : 'Total';
    rows.push([
      totalLabel,
      `${rentable.length} ${rentable.length === 1 ? 'unit' : 'units'}`,
      '',
      ...(includeStatus ? [''] : []),
      '',
      `Occupied: ${buildingOccupied}/${denom}`,
      `Occupancy: ${denom > 0 ? `${pct}%` : 'n/a'}`,
    ]);
    rows.push([]); // spacer between buildings

    portfolioOccupied += buildingOccupied;
    portfolioDenom += denom;
    void idx;
  });

  // Portfolio-wide total
  const portfolioPct = portfolioDenom > 0 ? Math.round((portfolioOccupied / portfolioDenom) * 100) : 0;
  rows.push(['Portfolio total']);
  rows.push([
    'Total rentable units',
    portfolioDenom,
    '',
    ...(includeStatus ? [''] : []),
    '',
    `Occupied: ${portfolioOccupied}/${portfolioDenom}`,
    `Occupancy: ${portfolioDenom > 0 ? `${portfolioPct}%` : 'n/a'}`,
  ]);

  return rows;
}

function rowsToCsv(rows) {
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return rows.map((r) => r.map(escape).join(',')).join('\r\n');
}

function computeColumnWidths(rows) {
  const widths = [];
  rows.forEach((r) => {
    r.forEach((cell, i) => {
      const len = cell === null || cell === undefined ? 0 : String(cell).length;
      widths[i] = Math.max(widths[i] || 0, len);
    });
  });
  return widths;
}

function downloadBlob(text, filename, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
